import { TableVirtuoso } from 'react-virtuoso';
import { DataTableSortableProps, DataTableRow, DataTableVariant } from './TableTypes';
import { TableLoader } from './TableLoader';
import { TableRowProps, Stack } from '@mui/material';
import { forwardRef, useMemo, useEffect, useRef } from 'react';
import TableRow from './TableRow';
import { ReachedLastRow } from './ReachedLastRow';
import { colors } from '@sweep-io/sweep-design';
import { useVirtualScrollTable } from './useVirtualScrollTable';
import { useVirtualTableComponents } from './hooks/useVirtualTableComponents';

/**
 * Supports out of the box support of virtual scrolling for smooth big amounts of data rendering
 */
export function VirtualScrollDataTable<TRow extends DataTableRow>({
  rows,
  columns: allColumns,
  onRowClick,
  containerRef,
  onSortChange,
  defaultSortState,
  tableEmptyStateJsx,
  sxRowFunction,
  isLoading,
  variant = DataTableVariant.medium,
  reachedLastRow,
  hideChevronButton,
  noTableBorder,
  tableId,
  exposeVirtuosoRef,
}: Pick<
  DataTableSortableProps<TRow>,
  | 'onRowClick'
  | 'columns'
  | 'rows'
  | 'containerRef'
  | 'onSortChange'
  | 'defaultSortState'
  | 'tableEmptyStateJsx'
  | 'isLoading'
  | 'variant'
  | 'sxRowFunction'
  | 'reachedLastRow'
  | 'hideChevronButton'
  | 'tableId'
> & { noTableBorder?: boolean; exposeVirtuosoRef?: boolean }) {
  const { noActionColumns, tableRef, internalColumnWidths, renderHeader } = useVirtualScrollTable({
    columns: allColumns,
    rows,
    containerRef,
    onSortChange,
    defaultSortState,
    tableId,
    variant,
    enabled: true,
  });

  const CustomTableRow = useMemo(() => {
    return forwardRef<any, TableRowProps>((props: any, ref) => {
      const row = props.item;
      const idx = props['data-index'];

      return (
        <>
          <TableRow
            hideChevronButton={hideChevronButton}
            data-index={idx}
            ref={ref}
            row={row}
            columns={noActionColumns}
            sxRowFunction={sxRowFunction}
            tableRowProps={{ id: `table_row_${row.id}` }}
            onClick={
              onRowClick
                ? (e, column) => {
                    onRowClick && onRowClick(e, row, column);
                  }
                : undefined
            }
            variant={variant}
            internalColumnWidths={internalColumnWidths}
            {...props}
          />

          {reachedLastRow && idx === reachedLastRow?.rowsMaxCount - 1 && (
            <ReachedLastRow
              columnsCount={noActionColumns.length}
              reachedLastRowTxt={reachedLastRow.txt}
              scrollToTop={() => {
                (containerRef?.current as any)?.scrollToIndex({
                  index: 0,
                  top: 0,
                  behavior: 'smooth',
                });
              }}
            />
          )}
        </>
      );
    });
  }, [
    hideChevronButton,
    noActionColumns,
    sxRowFunction,
    onRowClick,
    variant,
    internalColumnWidths,
    reachedLastRow,
    containerRef,
  ]);

  const components = useVirtualTableComponents({
    rows,
    tableEmptyStateJsx,
    customTableRow: CustomTableRow,
  });

  const stackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (exposeVirtuosoRef && stackRef.current && tableRef.current) {
      (stackRef.current as any).__virtuosoRef = tableRef.current;
    }
  }, [exposeVirtuosoRef, tableRef, stackRef]);

  return (
    <Stack
      ref={stackRef}
      height="100%"
      position="relative"
      data-table-ref={tableId}
      sx={{
        border: noTableBorder ? 'none' : `1px solid ${colors.grey[120]}`,
        borderRadius: noTableBorder ? 'none' : '8px',
        overflow: 'hidden',
      }}
    >
      {isLoading && <TableLoader />}

      <TableVirtuoso
        ref={tableRef}
        totalCount={rows.length}
        data={rows}
        components={components}
        fixedHeaderContent={renderHeader}
      />
    </Stack>
  );
}
